/**
 * 2D·3D 공통 이슈 그리기 손짓 규칙 (annotation-tool-interaction 스펙).
 * 점 배열은 각 지도 어댑터가 소유하고, 여기서는 판정만 한다.
 */

export type DrawToolKind =
  | "point"
  | "text"
  | "line"
  | "polygon"
  | "rectangle"
  | "anchor";

export const DRAW_MIN_POINTS: Record<DrawToolKind, number> = {
  point: 1,
  text: 1,
  line: 2,
  polygon: 3,
  rectangle: 2,
  anchor: 1,
};

/** 도구가 어떤 손짓에서 끝나는지 — 최소 점 개수(`DRAW_MIN_POINTS`)로는 갈리지 않는다(사각형 2·앵커 1 이 같은 두 번째 클릭 완료). 좌클릭 판정과 안내 칩이 이 표 하나를 읽는다 */
export const DRAW_FINISH_MODE: Record<
  DrawToolKind,
  "firstClick" | "secondClick" | "doubleClick"
> = {
  point: "firstClick",
  text: "firstClick",
  line: "doubleClick",
  polygon: "doubleClick",
  rectangle: "secondClick",
  anchor: "secondClick",
};

/** 첫 좌클릭에 바로 끝나는 도구인지 — 점·글자 */
export function finishesOnFirstClick(kind: DrawToolKind): boolean {
  return DRAW_FINISH_MODE[kind] === "firstClick";
}

/** 두 번째 좌클릭에 끝나는 도구인지 — 사각형(대각 끝점)과 앵커(3D 수직 측정의 대상점) */
export function finishesOnSecondClick(kind: DrawToolKind): boolean {
  return DRAW_FINISH_MODE[kind] === "secondClick";
}

export type LeftClickResolution = "add" | "finish";

interface ResolveLeftClickInput {
  kind: DrawToolKind;
  pointCount: number;
}

export function isFinishable(kind: DrawToolKind, pointCount: number): boolean {
  return pointCount >= DRAW_MIN_POINTS[kind];
}

/**
 * 좌클릭 판정 — 점·글자는 첫 클릭에 완료, 사각형과 앵커(3D 수직 측정)는 두 번째 클릭에 완료,
 * 선·면은 언제나 점 추가다. 재클릭 완료는 없다 — 선·면의 완료는 더블클릭(`isFinishable`)만이다.
 */
export function resolveLeftClick({
  kind,
  pointCount,
}: ResolveLeftClickInput): LeftClickResolution {
  if (finishesOnFirstClick(kind)) return "finish";
  if (finishesOnSecondClick(kind)) {
    return pointCount === 0 ? "add" : "finish";
  }
  return "add";
}

/**
 * 더블클릭 완료가 있는 도구인지 — 선·면만이다.
 * 점·글자는 첫 클릭에, 사각형과 앵커(3D 수직 측정)는 두 번째 클릭에 끝나므로 더블클릭 완료가 없다.
 * 2D 어댑터(`useDrawSketchGestures`)의 dblclick 배선과 안내 칩(`resolveToolGuideRows`)의
 * "더블클릭 — 완료" 줄이 이 판정 하나를 함께 읽는다.
 */
export function finishesByDoubleClick(kind: DrawToolKind): boolean {
  return DRAW_FINISH_MODE[kind] === "doubleClick";
}

export type RightClickResolution = "undo" | "exit";

/** 우클릭 판정 — 확정 점이 있으면 마지막 점 하나 되돌리기, 없으면 도구 종료 */
export function resolveRightClick(pointCount: number): RightClickResolution {
  return pointCount > 0 ? "undo" : "exit";
}
